/**
 * mantaSkeleton — a procedural manta-ray-skeleton silhouette generator. Produces
 * normalized geometry (0..1 box) from a seed: a diamond-shaped body plan with
 * wing spars, a central spine, a long tail spine, and rib struts across the
 * wings. The caller scales to the target width.
 *
 * Deterministic: all randomness via `seededRand` (NO Math.random). Same seed ⇒
 * identical geometry — goldens stable across reloads.
 *
 * Render-agnostic: returns points, not Pixi objects.
 */
import { seededRand } from '../atmospherePlacement';
import type { Point } from './types';

/** Configurable structural constants (BUILD_STANDARDS §1 — no magic numbers inline). */
const WING_SPAN = 0.9;          // wing span as fraction of total width
const WING_RIB_COUNT_MIN = 6;
const WING_RIB_COUNT_MAX = 10;
const WING_RIB_LENGTH = 0.22;   // max rib length (fraction of height)
const BODY_LENGTH = 0.35;       // central body length (fraction of width)
const BODY_HEIGHT = 0.18;       // central body height (fraction of height)
const TAIL_LENGTH = 0.25;       // tail spine length (fraction of width)
const TAIL_START = 0.55;        // where the tail begins (fraction of width)

/**
 * Generate a manta-skeleton silhouette in a normalized 0..1 box.
 * Width = 1.0 (head at x=0.45 center, wings spread left-right, tail at x=1).
 * The manta is wider than it is long — the "width" axis is the wing span.
 */
export function mantaSkeleton(seed: number, detail = 1): {
  outline: Point[];
  parts: Array<{ points: Point[] }>;
  bbox: { w: number; h: number };
} {
  const wingRibs = Math.round(
    WING_RIB_COUNT_MIN + seededRand(seed) * (WING_RIB_COUNT_MAX - WING_RIB_COUNT_MIN),
  ) * Math.max(0.5, Math.min(1.5, detail));

  const parts: Array<{ points: Point[] }> = [];

  // Central body: a diamond/rhombus at the center of the wing span.
  const bodyCx = BODY_LENGTH * 0.5;
  const bodyCy = 0.5;
  const bodyOutline: Point[] = [
    { x: bodyCx - BODY_LENGTH * 0.5, y: bodyCy },            // head (front)
    { x: bodyCx, y: bodyCy - BODY_HEIGHT * 0.5 },            // top
    { x: bodyCx + BODY_LENGTH * 0.5, y: bodyCy },            // tail junction
    { x: bodyCx, y: bodyCy + BODY_HEIGHT * 0.5 },            // bottom
  ];
  parts.push({ points: bodyOutline });

  // Wing spars: two long bones from the body center to each wingtip.
  const wingTipY = 0.5;
  const wingTipLeftX = (1 - WING_SPAN) * 0.5;
  const wingTipRightX = 1 - (1 - WING_SPAN) * 0.5;
  parts.push({
    points: [
      { x: bodyCx, y: bodyCy },
      { x: wingTipLeftX, y: wingTipY },
    ],
  });
  parts.push({
    points: [
      { x: bodyCx, y: bodyCy },
      { x: wingTipRightX, y: wingTipY },
    ],
  });

  // Wing ribs: struts perpendicular to the wing spars, on both wings.
  for (let i = 0; i < wingRibs; i += 1) {
    const t = (i + 1) / (wingRibs + 1);
    const variation = 0.8 + seededRand(seed + i * 5.7) * 0.4;
    const ribLen = WING_RIB_LENGTH * variation * (1 - t * 0.3);
    // Left wing rib.
    const lx = bodyCx + (wingTipLeftX - bodyCx) * t;
    const ly = bodyCy + (wingTipY - bodyCy) * t;
    parts.push({
      points: [
        { x: lx, y: ly - ribLen * 0.5 },
        { x: lx, y: ly + ribLen * 0.5 },
      ],
    });
    // Right wing rib.
    const rx = bodyCx + (wingTipRightX - bodyCx) * t;
    const ry = bodyCy + (wingTipY - bodyCy) * t;
    parts.push({
      points: [
        { x: rx, y: ry - ribLen * 0.5 },
        { x: rx, y: ry + ribLen * 0.5 },
      ],
    });
  }

  // Tail spine: a long thin bone from the body to the tail tip.
  const tailBaseX = bodyCx + BODY_LENGTH * 0.5;
  const tailTipX = TAIL_START + TAIL_LENGTH;
  parts.push({
    points: [
      { x: tailBaseX, y: bodyCy },
      { x: tailTipX, y: bodyCy },
    ],
  });

  // Body outline: a diamond + wing curves + tail.
  const outline: Point[] = [];
  // Head → top wing curve (left side: from head to left wingtip).
  const wingCurveSegments = 12;
  for (let i = 0; i <= wingCurveSegments; i += 1) {
    const t = i / wingCurveSegments;
    // Smooth curve from head (bodyCx, bodyCy - BODY_HEIGHT*0.5) to left wingtip.
    const x = bodyCx + (wingTipLeftX - bodyCx) * t;
    const y = (bodyCy - BODY_HEIGHT * 0.5) + (wingTipY - (bodyCy - BODY_HEIGHT * 0.5)) * t
      - Math.sin(t * Math.PI) * WING_RIB_LENGTH * 0.3;
    outline.push({ x, y });
  }
  // Left wingtip → bottom → tail tip.
  for (let i = 0; i <= wingCurveSegments; i += 1) {
    const t = i / wingCurveSegments;
    const x = wingTipLeftX + (tailTipX - wingTipLeftX) * t;
    const y = wingTipY + (bodyCy + BODY_HEIGHT * 0.5 - wingTipY) * Math.sin(t * Math.PI * 0.5);
    outline.push({ x, y });
  }
  // Tail tip → bottom wing curve (right side: from tail to right wingtip).
  for (let i = wingCurveSegments; i >= 0; i -= 1) {
    const t = i / wingCurveSegments;
    const x = bodyCx + (wingTipRightX - bodyCx) * t;
    const y = (bodyCy + BODY_HEIGHT * 0.5) + (wingTipY - (bodyCy + BODY_HEIGHT * 0.5)) * t
      + Math.sin(t * Math.PI) * WING_RIB_LENGTH * 0.3;
    outline.push({ x, y });
  }
  // Right wingtip → head (top right curve).
  for (let i = wingCurveSegments; i >= 0; i -= 1) {
    const t = i / wingCurveSegments;
    const x = wingTipRightX + (bodyCx - wingTipRightX) * t;
    const y = wingTipY + ((bodyCy - BODY_HEIGHT * 0.5) - wingTipY) * Math.sin(t * Math.PI * 0.5);
    outline.push({ x, y });
  }

  return {
    outline,
    parts,
    bbox: { w: 1, h: WING_RIB_LENGTH + BODY_HEIGHT + 0.1 },
  };
}
